import React, { useState, useEffect } from 'react';
import { Avatar, Box, CircularProgress, Grid, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Cancel from '@mui/icons-material/Cancel';
import { v4 as uuidv4 } from 'uuid';
import { IMAGE_FORMATS } from '../../config/variables';
import Link from '../Link';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import { getFileFromInput, getFileImage, getExtension } from '../../lib/File';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';
import CloudStorage from '../../Services/CloudStorage';
import UserStore from '../../stores/observables/UserStore';

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    backgroundColor: `${theme.palette.secondary.main}26`,
    color: theme.palette.secondary.main,
    height: theme.spacing(7),
    width: theme.spacing(7),
  },
  photoWrapper: {
    paddingLeft: theme.spacing(2),
  },
  removePhoto: {
    cursor: 'pointer',
    fontSize: '20px',
    position: 'absolute',
    right: '-15px',
    top: '-10px',
  },
}));

interface Props {
  customKeyPath?: string;
  disabled?: boolean;
  fallback: string;
  onPhotoChange: (uri: string | undefined) => void;
  photo: string;
}

const PhotoEditor: React.FC<Props> = (props: Props): JSX.Element => {
  const { customKeyPath, disabled, fallback, photo, onPhotoChange } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [previewPhoto, setPreviewPhoto] = useState(photo || undefined);
  const organizationId = OrganizationsStore.getCurrent();
  const currentUserId = UserStore.get()!.id;
  const keyPath = customKeyPath || `profile/${currentUserId}/photo-`;

  useEffect(() => {
    if (initialLoad) {
      setPreviewPhoto(photo);
    }
  }, [photo, initialLoad]);

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = getFileFromInput(e);
    if (!file) return;
    getFileImage(file, {
      onSuccess: async (previewphoto: string) => {
        setLoading(true);
        try {
          const { uri } = await CloudStorage.uploadImage(
            { organizationId },
            file,
            `${keyPath}${Date.now()}-${uuidv4()}.${getExtension(file)}`,
            undefined,
            IMAGE_FORMATS
          );
          setInitialLoad(false);
          onPhotoChange(uri);
          setPreviewPhoto(previewphoto);
          setLoading(false);
        } catch (err) {
          SnackbarStore.show(err as string);
        }
      },
      supportedFormats: IMAGE_FORMATS,
    });
  };

  const onRemovePhoto = () => {
    setPreviewPhoto('');
    onPhotoChange('');
  };

  const renderPhoto = () => (
    <Box position="relative">
      {previewPhoto && (
        <Cancel className={classes.removePhoto} onClick={onRemovePhoto} />
      )}
      <Avatar
        classes={{
          root: classes.avatar,
        }}
        src={previewPhoto}
      >
        {fallback}
      </Avatar>
    </Box>
  );

  return (
    <Box>
      <Grid alignItems="center" container>
        <Grid item>
          {loading ? (
            <CircularProgress color="secondary" size={32} />
          ) : (
            renderPhoto()
          )}
        </Grid>
        <Grid
          alignItems="flex-start"
          className={classes.photoWrapper}
          container
          direction="column"
          item
          spacing={2}
          xs
        >
          <Grid item component="label">
            <input
              disabled={disabled}
              accept={IMAGE_FORMATS.join(',')}
              id="photo-picker"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
              type="file"
            />
            {!previewPhoto && (
              <Link text="Add photo" dataTestId="link-add-photo" />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PhotoEditor;
